import { ImageName } from "@mcleod/images";
import {
    Button, CloneComponent, DataSourceExecutionEvent, DataSourceMode, Label, LookupModelSelectionEvent, Panel, Snackbar
} from "@mcleod/components";
import { AutogenLayoutDispatchBalance } from "./autogen/AutogenLayoutDispatchBalance";
import { ModelRow, ObjectUtil, StringUtil } from "@mcleod/core";
import { BalanceData } from "../BalanceData";
import { LMEUserSettings } from "@mcleod/general";
import { RowOrderPlanningProfile } from "../models/ModelOrderPlanningProfile";

export class DispatchBalance extends AutogenLayoutDispatchBalance {
    private _profileRow: RowOrderPlanningProfile;

    onLoad(): void | Promise<void> {
        this.loadInitialProfile();
    }

    /** This is an event handler for the afterExecution event of sourceDispatchBalance. */
    sourceDispatchBalanceAfterExecution(event: DataSourceExecutionEvent) {
        this.panelAreas.removeAll();
        this.fillDays();
        const balanceData = this.sourceDispatchBalance.activeRow.get("balance_data", {});
        this.panelEmpty.visible = ObjectUtil.isEmptyObject(balanceData);
        for (const [key, data] of Object.entries(balanceData).sort()) {
            const area = StringUtil.isEmptyString(key) ? "Unspecified" : key;
            this.labelAreaTitle.text = `Area ${area} Subtotals`;
            const areaTab = CloneComponent.clone({ component: this.panelArea, appendComponentId: false }) as Panel;
            const panelZones = areaTab.findComponentById("panelZones") as Panel;
            const buttonExpand = areaTab.findComponentById("buttonExpand") as Button;
            const panelAreaHeader = areaTab.findComponentById("panelAreaHeader") as Panel;
            this.addExpandListener(panelAreaHeader, panelZones, buttonExpand, (expanded) => {
                this.adjustDayHeaderWidth();
            });
            panelZones.removeAll();
            const zones = data as any[];
            zones.forEach((zoneData, index) => {
                const isAreaSubtotal = index === zones.length-1;
                const tabTitle = isAreaSubtotal ? `Area ${area} Subtotals` : `Zone ${zoneData.zone}`;
                this.labelZoneTitle.text = tabTitle;
                const panelZone = CloneComponent.clone({ component: this.panelZone, appendComponentId: false, overrideProps: { padding: 0 }}) as Panel;
                const zoneRow = new ModelRow(this.sourceDispatchBalance.url, false, zoneData);
                if (isAreaSubtotal) {
                    const panelAreaBalance = areaTab.findComponentById("panelAreaBalance") as Panel;
                    areaTab.displayData(zoneRow, null, 0);
                    this.setupBalanceForHeader(panelAreaBalance);
                } else {
                    panelZone.visible = true;
                    const panelZoneExpand = panelZone.findComponentById("panelZoneExpand") as Panel;
                    const buttonDropdown = panelZone.findComponentById("buttonDropdown") as Button;
                    const balanceData = panelZoneExpand.findComponentById("layoutBalanceData") as BalanceData;
                    balanceData.addLayoutLoadListener(() => {
                        balanceData.sourceDispatchBalance.setRowsAndMode(DataSourceMode.UPDATE, [zoneRow]);
                        const panelZoneHeader = panelZone.findComponentById("panelZoneHeader") as Panel;
                        const panelZoneBalance = panelZone.findComponentById("panelZoneBalance") as Panel;
                        const zoneHeaderBalance = CloneComponent.clone({ component: balanceData.panelBalance, appendComponentId: false, overrideProps: { padding: 0, paddingRight: 26 }}) as Panel;
                        this.setupBalanceForHeader(zoneHeaderBalance);
                        panelZoneBalance.add(zoneHeaderBalance);
                        this.addExpandListener(panelZoneHeader, panelZoneExpand, buttonDropdown, (expanded) => {
                            zoneHeaderBalance.visible = !expanded;
                        });
                    });
                    panelZones.add(panelZone);
                }
            });
            this.panelAreas.add(areaTab);
        }
        this.adjustDayHeaderWidth();
    }

    addExpandListener(clickablePanel: Panel, expandedPanel: Panel, button: Button, callback?: (expanded: boolean) => void) {
        button.addClickListener(() => {
            let expanded:boolean = false;
            if (button.id == "buttonDropdown") {
                button.imageName = button.imageName == ImageName.addInBox ? ImageName.checkboxIndeterminate : ImageName.addInBox;
                expanded = button.imageName == "checkboxIndeterminate";
            } else {
                button.imageRotation = button.imageRotation == 90 ? 0 : 90;
                expanded = button.imageRotation == 0;
            }
            expandedPanel.visible = expanded;
            callback?.(expanded);
        });
        clickablePanel.addClickListener(() => button.clicked());
    }

    fillDays() {
        for (let i = 1; i <= 7; i++) {
            const date = new Date();
            date.setDate(date.getDate() + i);
            this.sourceDispatchBalance.activeRow.set("day_" + (i+1), date);
        }
    }

    adjustDayHeaderWidth() {
        this.panelDayHeader.components.forEach((comp: Panel) => {
            comp.width = this.panelAreas.element.scrollHeight > this.panelAreas.element.clientHeight ? "13.7%" : "14%";
        });
    }

    setupBalanceForHeader(panelBalance: Panel) {
        for (const comp of panelBalance.components) {
            if (comp instanceof Label) {
                if (comp.id == "labelBalance") {
                    comp.text = "";
                    comp.width = comp.padding = 0;
                } else {
                    comp.color = parseInt(comp.text) < 0 ? "red" : "default";
                    comp.width = "14%";
                }
            }
        }
    }

    /** Profile Logic */
    async loadInitialProfile() {
        const profileId = LMEUserSettings.get()?.order_planning_profile;

        if (profileId == null)
            Snackbar.showWarningSnackbar("Please set a default operations management profile to see data on the balance widget.");
        else {
            this.sourceDispatchBalance.searchRow.setValueAndLookupModelData("id", profileId, { id: profileId });
            this.textboxId.displayData(this.sourceDispatchBalance.searchRow, null, 0);
        }

        this.textboxId.addBeforeLookupModelSearchListener(event => {
            if (this._profileRow != null && this._profileRow.get("id", "") === this.textboxId.text)
                event.filter = null;
        });

        this.textboxId.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.titleBar.refresh();
            }
        });
    }
}
